/**
 * NotificationFallbackBanner (`notif-4`) — "nobody is set to receive this".
 * Implements `docs/NOTIFICATIONS.md` Sec. 6.
 *
 * Rule Sec. 1.1 says a due filing must reach *someone*, so when routing resolves to
 * nobody the spine falls back to the org's published contact. That keeps Karr from
 * going silent — but it is a **safety net, not a setting**: it messages whoever
 * happens to own the address on the letterhead, which is the conflation this whole
 * lane exists to end. So say so, once, and offer the fix.
 *
 * It asks the REAL resolver (`get_notification_recipients`) rather than guessing
 * from members or prefs — the banner and the sender therefore agree by
 * construction. `filing.gst.due` is the probe: fallback happens when *no member*
 * resolves *any* channel (everyone opted out, or the org has only auditors), which
 * is a property of the org's routing rather than of one event, and every filing
 * event shares the same role defaults.
 *
 * Renders nothing in the normal case, so it's safe to drop in anywhere.
 *
 * @since 1.0.0 (notif-4)
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { supabase } from '@/db/supabase';
import { useAuth } from '@/shared/hooks/useAuth';

const PROBE_EVENT = 'filing.gst.due';

interface Row { recipient_kind: string; channels: string[] }

export function NotificationFallbackBanner({ className = '', onChoose }: {
    className?: string;
    /** Where "choose who" goes. Defaults to Settings → Notifications. */
    onChoose?: () => void;
}) {
    const currentOrg = useAuth((s) => s.currentOrg);
    const orgId = (currentOrg as { id?: string } | null)?.id;
    const [state, setState] = useState<'ok' | 'fallback' | 'unreachable'>('ok');

    useEffect(() => {
        if (!orgId) return;
        let cancelled = false;
        void (async () => {
            const { data, error } = await supabase.rpc('get_notification_recipients', {
                p_org: orgId, p_event_key: PROBE_EVENT,
            });
            if (cancelled || error) return; // a probe failure must never itself nag
            const rows = (data ?? []) as Row[];
            if (rows.length === 0) return;
            if (!rows.every((r) => r.recipient_kind === 'fallback')) return;
            setState(rows.every((r) => r.channels.length === 0) ? 'unreachable' : 'fallback');
        })();
        return () => { cancelled = true; };
    }, [orgId]);

    if (state === 'ok') return null;

    // Settings deep-links by hash (#billing, #notifications) — not a search param.
    const choose = onChoose ?? (() => { window.location.href = '/settings#notifications'; });
    const unreachable = state === 'unreachable';

    return (
        <div
            className={`flex items-start gap-3 rounded-lg border px-4 py-3 ${
                unreachable
                    ? 'border-red-200 bg-red-50 dark:border-red-500/30 dark:bg-red-500/10'
                    : 'border-amber-200 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10'
            } ${className}`}
        >
            <AlertTriangle
                size={17}
                className={`mt-0.5 flex-shrink-0 ${unreachable ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}
            />
            <div className="min-w-0 flex-1">
                <p className={`text-sm font-medium ${unreachable ? 'text-red-900 dark:text-red-200' : 'text-amber-900 dark:text-amber-200'}`}>
                    {unreachable
                        ? 'Karr has no way to reach anyone about your filings.'
                        : 'Filing reminders are going to your published business contact.'}
                </p>
                <p className={`mt-0.5 text-xs leading-relaxed ${unreachable ? 'text-red-700 dark:text-red-300/90' : 'text-amber-700 dark:text-amber-300/90'}`}>
                    {unreachable
                        ? 'Nobody is set to receive them, and there’s no published contact to fall back on — so a due date could pass in silence.'
                        : 'Nobody is set to receive them, so they go to the address on your letterhead — whoever happens to own that inbox.'}{' '}
                    Choose who should get them.
                </p>
            </div>
            <button
                type="button"
                onClick={choose}
                className={`flex flex-shrink-0 items-center gap-1 self-center rounded-lg px-2.5 py-1.5 text-xs font-medium focus:outline-none focus:ring-2 ${
                    unreachable
                        ? 'text-red-700 hover:bg-red-100 focus:ring-red-400 dark:text-red-300 dark:hover:bg-red-500/20'
                        : 'text-amber-800 hover:bg-amber-100 focus:ring-amber-400 dark:text-amber-300 dark:hover:bg-amber-500/20'
                }`}
            >
                Choose who <ArrowRight size={12} />
            </button>
        </div>
    );
}
